/**
 * The client portal's API client.
 *
 * Everything goes to the portal's own corner of the API — `/api/v1/portal` — so
 * a path here reads the way the portal's routes do (`/tickets`, `/files/upload`).
 * Every response is `{ success, data, message }` (or `{ success:false, error }`);
 * this unwraps the envelope once. A 401 triggers one refresh attempt, and
 * concurrent 401s share that single refresh.
 *
 * The portal keeps its own tokens under its own keys: clients.viganium.net is
 * its own origin anyway, and a client account is not a staff account.
 */

const BASE = '/api/v1/portal';

const ACCESS_KEY = 'viganium.portal.accessToken';
const REFRESH_KEY = 'viganium.portal.refreshToken';

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, error: ApiErrorShape) {
    super(error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export const tokens = {
  get access() {
    return storage()?.getItem(ACCESS_KEY) ?? null;
  },
  get refresh() {
    return storage()?.getItem(REFRESH_KEY) ?? null;
  },
  set(access: string, refresh: string) {
    storage()?.setItem(ACCESS_KEY, access);
    storage()?.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    storage()?.removeItem(ACCESS_KEY);
    storage()?.removeItem(REFRESH_KEY);
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      tokens.set(body.data.accessToken, body.data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshInFlight = null), 0);
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean;
}

function signedOut() {
  tokens.clear();
  window.dispatchEvent(new CustomEvent('viganium:portal-signed-out'));
}

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, signal } = options;

  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(url.toString().replace(window.location.origin, ''), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 401 && !options._retried && tokens.refresh) {
    if (await refreshTokens()) return request<T>(path, { ...options, _retried: true });
    signedOut();
    throw new ApiError(401, { code: 'TOKEN_INVALID', message: 'Your session has expired.' });
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    const error: ApiErrorShape = payload?.error ?? {
      code: 'NETWORK_ERROR',
      message: `Request failed (${res.status}).`,
    };
    if (res.status === 401) signedOut();
    throw new ApiError(res.status, error);
  }

  return payload?.data as T;
}

interface UploadOptions {
  kind?: 'FILE' | 'VOICE_NOTE';
  durationMs?: number;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** `POST /portal/files/uploads` — the file is checked, and comes back with a ticket and where its bytes go. */
interface UploadStart {
  ticket: string;
  upload: { method: 'PUT'; url: string; headers: Record<string, string> };
}

/**
 * Uploads one file, voice note or video, reporting progress as it goes, and
 * returns it to be attached by id when the ticket, answer or change request is
 * saved.
 *
 * Three steps: the API checks the file and says where its bytes go — straight
 * into Cloudflare R2 through a link it signed, or to the API itself when files
 * are kept on the server — the bytes go there, and the API keeps the file. An
 * API from before this offers only the one-step upload, which is used then.
 */
export async function uploadFile(blob: Blob, name: string, options: UploadOptions = {}): Promise<UploadedFile> {
  let start: UploadStart;
  try {
    start = await request<UploadStart>('/files/uploads', {
      method: 'POST',
      body: {
        name,
        type: blob.type,
        size: blob.size,
        kind: options.kind,
        durationMs: options.durationMs === undefined ? undefined : Math.round(options.durationMs),
      },
      signal: options.signal,
    });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return uploadWhole(blob, name, options);
    throw e;
  }
  await sendBytes(blob, start.upload, options);
  const file = await request<UploadedFile>('/files/uploads/complete', {
    method: 'POST',
    body: { ticket: start.ticket },
    signal: options.signal,
  });
  return { ...file, uploadedBy: null };
}

/** The bytes, to where the API said — XMLHttpRequest, since `fetch` cannot report an upload's progress. */
function sendBytes(blob: Blob, target: UploadStart['upload'], options: UploadOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
      return;
    }
    // R2 is another origin; the API's own route is this one.
    const toStorage = new URL(target.url, window.location.origin).origin !== window.location.origin;
    const xhr = new XMLHttpRequest();
    xhr.open(target.method, target.url);
    // Exactly these: an R2 link is signed with them.
    for (const [header, value] of Object.entries(target.headers)) xhr.setRequestHeader(header, value);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total);
    };
    const abort = () => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    const settle = () => options.signal?.removeEventListener('abort', abort);

    xhr.onload = () => {
      settle();
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let payload: any = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        /* R2 answers in XML, a proxy in HTML — neither is our envelope */
      }
      const error: ApiErrorShape =
        payload?.error ??
        (xhr.status === 413
          ? { code: 'FILE_TOO_LARGE', message: 'The file is too large.' }
          : toStorage
            ? { code: 'FILE_STORAGE_REFUSED', message: `The file storage refused the upload (${xhr.status}).` }
            : { code: 'NETWORK_ERROR', message: `Upload failed (${xhr.status}).` });
      reject(new ApiError(xhr.status, error));
    };
    // A storage that does not let this page upload (its CORS policy) looks the same as no network.
    xhr.onerror = () => {
      settle();
      reject(
        new ApiError(
          0,
          toStorage
            ? { code: 'FILE_STORAGE_UNREACHABLE', message: 'The file storage could not be reached.' }
            : { code: 'NETWORK_ERROR', message: 'The upload was interrupted.' },
        ),
      );
    };
    xhr.onabort = () => {
      settle();
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
    };
    xhr.send(blob);
  });
}

/**
 * The one-step upload, `POST /portal/files/upload`: the whole file to the API in
 * one request. Only for an API that has no three-step upload yet.
 */
function uploadWhole(blob: Blob, name: string, options: UploadOptions = {}, retried = false): Promise<UploadedFile> {
  return new Promise<UploadedFile>((resolve, reject) => {
    const form = new FormData();
    // Fields before the file, so the server has them when the file arrives.
    if (options.kind) form.append('kind', options.kind);
    if (options.durationMs !== undefined) form.append('durationMs', String(Math.round(options.durationMs)));
    form.append('file', blob, name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/files/upload`);
    const access = tokens.access;
    if (access) xhr.setRequestHeader('Authorization', `Bearer ${access}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total);
    };
    const abort = () => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });

    xhr.onload = async () => {
      options.signal?.removeEventListener('abort', abort);
      if (xhr.status === 401 && !retried && tokens.refresh && (await refreshTokens())) {
        uploadWhole(blob, name, options, true).then(resolve, reject);
        return;
      }
      let payload: any = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        /* the proxy's own error page, not our envelope */
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload?.data) {
        return resolve({ ...(payload.data as UploadedFile), uploadedBy: null });
      }
      const error: ApiErrorShape =
        payload?.error ??
        (xhr.status === 413
          ? { code: 'FILE_TOO_LARGE', message: 'The file is too large.' }
          : { code: 'NETWORK_ERROR', message: `Upload failed (${xhr.status}).` });
      reject(new ApiError(xhr.status, error));
    };
    xhr.onerror = () => reject(new ApiError(0, { code: 'NETWORK_ERROR', message: 'The upload was interrupted.' }));
    xhr.onabort = () => reject(new DOMException('The upload was cancelled.', 'AbortError'));
    xhr.send(form);
  });
}

export const api = {
  get: <T>(path: string, params?: RequestOptions['params']) => request<T>(path, { params }),
  post: <T>(path: string, body?: unknown, params?: RequestOptions['params']) =>
    request<T>(path, { method: 'POST', body, params }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
};

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface Named {
  name?: string | null;
  nameAr?: string | null;
  fullName?: string;
  fullNameAr?: string | null;
}

export interface Account {
  id: string;
  username: string;
  locale: string;
  lastLoginAt: string | null;
  client: { id: string; code: string; companyName: string; companyNameAr: string | null };
  /**
   * The contact this sign-in is for; null for the company's own (2026-10-11).
   * An API from before sends none.
   */
  contact?: { id: string; name: string; jobTitle: string | null } | null;
}

/**
 * Which of the client's people raised or wrote something: `you` for the
 * reader's own; a contact's name; no name for the company's own sign-in, which
 * the portal calls by the company's name.
 */
export interface ClientPerson {
  you: boolean;
  name: string | null;
  jobTitle: string | null;
}

/** A department the client may send a ticket to; `isDefault` is the one the form starts on. */
export interface PortalDepartment {
  id: string;
  name: string;
  nameAr: string | null;
  isDefault: boolean;
}

export interface ProjectRef {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  status?: string;
  /** False for a project whose support period has ended (completed): it takes no new tickets. */
  takesTickets?: boolean;
}

/** A file as the portal hands it out: never a storage path, always signed links. */
export interface UploadedFile {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  kind: 'FILE' | 'VOICE_NOTE';
  durationMs: number | null;
  createdAt: string;
  /** Always empty on the portal; kept so the shared file components fit. */
  uploadedBy: { id: string; fullName: string; fullNameAr?: string | null } | null;
  url: string;
  downloadUrl: string;
}

/** The shape the file list draws — a file and when it was added. */
export interface Attachment {
  id: string;
  commentId: string | null;
  createdAt: string;
  file: UploadedFile;
}

/** The portal sends bare files; the shared list wants attachments. */
export function asAttachments(files: Array<Omit<UploadedFile, 'uploadedBy'>> | undefined): Attachment[] {
  return (files ?? []).map((file) => ({
    id: file.id,
    commentId: null,
    createdAt: file.createdAt,
    file: { ...file, uploadedBy: null },
  }));
}

export interface FilePolicy {
  /** Pictures, documents and recordings. */
  maxUploadMb: number;
  /** Videos. An API from before videos sends none. */
  maxVideoMb?: number;
  extensions: string[];
  /** The extensions that are always a video; a webm or an ogg goes by the browser's type. */
  videoExtensions?: string[];
  maxFilesPerPost: number;
  maxVoiceNoteSeconds: number;
}

export type TicketStatus =
  | 'NEW'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'WAITING_REQUESTER'
  | 'RESOLVED'
  | 'CLOSED'
  | 'REOPENED'
  | 'CANCELLED';

export type TicketGroup = 'open' | 'inProgress' | 'finished' | 'cancelled';
export type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type ChangeRequestStatus = 'NEW' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED' | 'DONE';

export interface TicketRow {
  id: string;
  number: string;
  subject: string;
  status: TicketStatus;
  group: TicketGroup;
  priority: Priority;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  raisedByClient: boolean;
  /** Which of the client's people raised it here — none when we raised it. An API from before sends none. */
  raisedBy?: ClientPerson | null;
  /** Which of the client's own people asked us to raise it — none when the client raised it here. */
  requestedBy: { name: string; jobTitle: string | null } | null;
  project: ProjectRef | null;
  replies: number;
}

export interface TicketReply {
  id: string;
  body: string;
  createdAt: string;
  fromClient: boolean;
  /** For the client's own replies: which of its people wrote it. An API from before sends none. */
  clientAuthor?: ClientPerson | null;
  author: { fullName: string; fullNameAr: string | null; jobTitle: string | null } | null;
  attachments: UploadedFile[];
}

export interface TicketDetail extends Omit<TicketRow, 'replies'> {
  description: string | null;
  closedAt: string | null;
  resolution: string | null;
  /** The department it went to. An API from before sends none. */
  department?: { name: string; nameAr: string | null } | null;
  handledBy: { fullName: string; fullNameAr: string | null } | null;
  canReply: boolean;
  attachments: UploadedFile[];
  replies: TicketReply[];
}

export interface ChangeRequestRow {
  id: string;
  number: string;
  title: string;
  status: ChangeRequestStatus;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  project: ProjectRef | null;
  /** Which of the client's people raised it. An API from before sends none. */
  raisedBy?: ClientPerson | null;
  files: number;
  /** Messages on it, both ways. */
  replies: number;
}

export interface ChangeRequestEvent {
  id: string;
  fromStatus: ChangeRequestStatus | null;
  status: ChangeRequestStatus;
  note: string | null;
  at: string;
  byClient: boolean;
  by: { fullName: string; fullNameAr: string | null } | null;
}

export interface ChangeRequestDetail extends Omit<ChangeRequestRow, 'files' | 'replies'> {
  details: string | null;
  reviewedAt: string | null;
  attachments: UploadedFile[];
  events: ChangeRequestEvent[];
  /** The client writes on it until it is done. */
  canReply: boolean;
  /** The messages between the client and us — shaped as a ticket's replies are. */
  replies: TicketReply[];
}

export type ComplaintStatus = 'NEW' | 'IN_REVIEW' | 'CLOSED';

/** Someone the client may complain about: a person on its projects, with the projects. */
export interface ComplaintPerson {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  projects: ProjectRef[];
}

/** Whom a complaint is about — a name and a job title; none once they have left us. */
export interface ComplaintAbout {
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
}

export interface ComplaintRow {
  id: string;
  number: string;
  title: string;
  status: ComplaintStatus;
  /** Our latest note — on a closed one, our answer. */
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  about: ComplaintAbout | null;
  /** Which of the client's people made it. */
  raisedBy: ClientPerson | null;
  files: number;
}

export interface ComplaintEvent {
  id: string;
  fromStatus: ComplaintStatus | null;
  status: ComplaintStatus;
  note: string | null;
  at: string;
  byClient: boolean;
}

export interface ComplaintDetail extends Omit<ComplaintRow, 'files'> {
  details: string | null;
  reviewedAt: string | null;
  attachments: UploadedFile[];
  events: ComplaintEvent[];
}

export interface TeamMember {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  email: string;
  phone: string | null;
  onShiftSince: string | null;
  onBreak: boolean;
}

export interface Dashboard {
  tickets: {
    total: number;
    open: number;
    inProgress: number;
    finished: number;
    cancelled: number;
    byStatus: Record<TicketStatus, number>;
  };
  tasks: { total: number; byStatus: Record<string, number> };
  changeRequests: { total: number; open: number; byStatus: Record<ChangeRequestStatus, number> };
  /** `supportUntil` — the last day of a project's support period, while it is in one. */
  projects: Array<ProjectRef & { status: string; supportUntil?: string | null; tasks: number; tasksDone: number }>;
  team: TeamMember[];
  canRaiseTickets: boolean;
}
